
import React, { useState, useEffect, useRef } from 'react';
import { DateRangePicker, createStaticRanges } from 'react-date-range';
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subWeeks,
  subMonths,
  subDays,
  startOfYear,
  endOfYear,
  subYears,
  isBefore,
  isAfter,
  isValid,
  format
} from 'date-fns';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const DatePicker = ({ onChange, defaultRange, minDate, maxDate }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  // --- Effective bounds ------------------------------------------------------
  const maxDateStr = localStorage.getItem('max_date');
  const parsedMaxDate = maxDateStr ? new Date(maxDateStr) : null;

  const isValidDate = (d) => d instanceof Date && isValid(d);

  const effectiveMin = isValidDate(minDate) ? minDate : undefined;
  const lsMax = isValidDate(parsedMaxDate) ? parsedMaxDate : undefined;
  const propMax = isValidDate(maxDate) ? maxDate : undefined;
  const effectiveMax = lsMax ?? propMax ?? undefined;

  const shouldDisableAll = !effectiveMin && !effectiveMax;

  // Context for “Today”, etc. If out of bounds, we’ll clamp below anyway.
  const todayContext = effectiveMax ?? new Date();

  // --- Clamp helpers ---------------------------------------------------------
  const clampDate = (d) => {
    let x = d;
    if (effectiveMin && isBefore(x, effectiveMin)) x = effectiveMin;
    if (effectiveMax && isAfter(x, effectiveMax)) x = effectiveMax;
    return x;
  };

  const clampRange = ({ startDate, endDate }) => {
    let s = clampDate(startDate);
    let e = clampDate(endDate);
    if (isAfter(s, e)) s = e; // safety for very tight windows
    return { startDate: s, endDate: e };
  };

  const clampRangeArray = (arr) =>
    (arr || []).map((r) => ({ key: r.key || 'selection', ...clampRange(r) }));

  // --- Default / initial range ----------------------------------------------
  const getValidRange = () => {
    // pick an “end” (prefer effectiveMax; else today), then create a 6-day window
    const rawEnd = effectiveMax ?? new Date();
    const end = clampDate(rawEnd);

    let start = subDays(end, 5); // 5 days back = 6-day window
    start = clampDate(start);

    if (isAfter(start, end)) start = end;

    return [{ key: 'selection', startDate: start, endDate: end }];
  };
  const formatDateRange = () => {
  const range = state[0];
  if (!range.startDate || !range.endDate) {
    return 'No date available';
  }
  const formattedStart = format(range.startDate, 'dd/MM/yyyy');
  const formattedEnd = format(range.endDate, 'dd/MM/yyyy');
  return `${formattedStart} – ${formattedEnd}`;
  };

  const [state, setState] = useState(getValidRange());
  const [tempRange, setTempRange] = useState(getValidRange());

  // --- Static ranges (ALL clamped) ------------------------------------------
  const myStaticRanges = [
    { label: 'Today', range: () => clampRange({ startDate: todayContext, endDate: todayContext }) },
    { label: 'Yesterday', range: () => {
        const y = subDays(todayContext, 1);
        return clampRange({ startDate: y, endDate: y });
      },
    },
    { label: 'This Week', range: () => clampRange({
        startDate: startOfWeek(todayContext, { weekStartsOn: 0 }),
        endDate: endOfWeek(todayContext, { weekStartsOn: 0 }),
      }),
    },
    { label: 'Last Week', range: () => {
        const lw = subWeeks(todayContext, 1);
        return clampRange({
          startDate: startOfWeek(lw, { weekStartsOn: 0 }),
          endDate: endOfWeek(lw, { weekStartsOn: 0 }),
        });
      },
    },
    { label: 'This Month', range: () => clampRange({
        startDate: startOfMonth(todayContext),
        endDate: endOfMonth(todayContext),
      }),
    },
    { label: 'Last Month', range: () => {
        const lm = subMonths(todayContext, 1);
        return clampRange({
          startDate: startOfMonth(lm),
          endDate: endOfMonth(lm),
        });
      },
    },
  ];

  const customStaticRanges = [
    { label: 'This Year', range: () => clampRange({
        startDate: startOfYear(todayContext),
        endDate: endOfYear(todayContext),
      }),
    },
    { label: 'Last Year', range: () => {
        const ly = subYears(todayContext, 1);
        return clampRange({
          startDate: startOfYear(ly),
          endDate: endOfYear(ly),
        });
      },
    },
  ];

  const staticRanges = createStaticRanges(
    shouldDisableAll ? [] : [...myStaticRanges, ...customStaticRanges]
  );

  // --- Close on outside click ------------------------------------------------
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- Normalize + CLAMP incoming defaultRange ------------------------------
  const normalizeRange = (range) =>
    (range || []).map((r) => ({
      ...r,
      startDate: new Date(r.startDate),
      endDate: new Date(r.endDate),
      key: r.key || 'selection',
    }));
  useEffect(() => {
    if (
      defaultRange &&
      Array.isArray(defaultRange) &&
      defaultRange[0]?.startDate &&
      defaultRange[0]?.endDate
    ) {
      const bounded = clampRangeArray(normalizeRange(defaultRange));
      setState(bounded);
      setTempRange(bounded);
    } else {
      const initial = getValidRange();
      setState(initial);
      setTempRange(initial);
    }
    // Only re-run if raw inputs change
    // 🚨 DO NOT include effectiveMin / effectiveMax (they're recalculated each render)
  }, [defaultRange, minDate, maxDate, maxDateStr]);


  // --- Enforce clamping on every user change --------------------------------
  const handleRangeChange = (item) => {
    if (shouldDisableAll) return;
    const clamped = clampRangeArray([item.selection]);
    setTempRange(clamped);
  };

  const handleApply = () => {
    const clamped = clampRangeArray(tempRange);
    setState(clamped);
    onChange && onChange(clamped);
    setOpen(false);
  };

  const formatDate = (date) => {
    if (!date) return 'No date available';
    return date.toLocaleDateString('en-GB'); // 'dd/mm/yyyy' format
  };

  // --- Render ----------------------------------------------------------------
  return (
    <div
      className="inline-block text-left w-full max-w-md custom-date-picker"
      ref={ref}
      style={{ position: 'relative', fontSize: 'smaller' }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full border rounded date-picker-btn text-black text-left"
        disabled={shouldDisableAll}
      >
        {formatDate(state[0].startDate)} &nbsp;-&nbsp; {formatDate(state[0].endDate)} &nbsp;

        <i className="bi bi-calendar" />
      </button>

      {open && (
        <div className="absolute z-50 mt-2 shadow-lg overflow-x-auto datepicker-outer-div" style={{ zIndex: 99999999 }}>
          <DateRangePicker
            onChange={handleRangeChange}
            showSelectionPreview
            moveRangeOnFirstSelection={false}
            months={1}
            ranges={tempRange}
            showDateDisplay
            direction={window.innerWidth < 768 ? 'vertical' : 'horizontal'}
            staticRanges={staticRanges}
            inputRanges={[]}
            // IMPORTANT: do NOT fall back to new Date() here
            minDate={shouldDisableAll ? undefined : effectiveMin}
            maxDate={shouldDisableAll ? undefined : effectiveMax}
            disabled={shouldDisableAll}
          />
          <div className="p-2 text-right apply-btn-datepicker">
            <button className="btn auth-btn-datepicker mb-2" onClick={handleApply}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
